import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

function creationTime(createdAt) {
  const date = createdAt ? new Date(createdAt) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short" })
    : "Creation time unavailable";
}

export default function MeetingSelector({ meetings, selected, onChange }) {
  const id = useId();
  const root = useRef(null);
  const control = useRef(null);
  const list = useRef(null);
  const typed = useRef({ text: "", at: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState({ above: false, maxHeight: 320, controlHeight: 44 });
  const selectedIndex = meetings.findIndex(meeting => meeting.meetingId === selected);
  const current = meetings[selectedIndex];
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const rect = control.current.getBoundingClientRect();
      const below = Math.max(0, window.innerHeight - rect.bottom - 12);
      const above = Math.max(0, rect.top - 12);
      const desired = Math.min(320, list.current.scrollHeight);
      const openAbove = below < desired && above > below;
      setPlacement({ above: openAbove, maxHeight: Math.min(320, openAbove ? above : below), controlHeight: rect.height });
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, meetings.length]);
  useEffect(() => {
    if (!open) return;
    const menu = list.current;
    const option = menu.querySelector(`[data-option-index="${active}"]`);
    if (!option) return;
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < menu.scrollTop) menu.scrollTop = top;
    else if (bottom > menu.scrollTop + menu.clientHeight) menu.scrollTop = bottom - menu.clientHeight;
  }, [active, open, placement.maxHeight]);
  function show() { setActive(Math.max(0, selectedIndex)); typed.current = { text: "", at: 0 }; setOpen(true); }
  function choose(index) { if (meetings[index]) onChange(meetings[index].meetingId); setOpen(false); }
  function keyDown(event) {
    const { key } = event;
    if (key === "Escape" || key === "Tab") { setOpen(false); if (key === "Escape") event.preventDefault(); return; }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(key)) {
      event.preventDefault();
      if (!open) show();
      setActive(index => key === "Home" ? 0 : key === "End" ? meetings.length - 1
        : !open ? Math.max(0, selectedIndex) : Math.max(0, Math.min(meetings.length - 1, index + (key === "ArrowDown" ? 1 : -1))));
    } else if (key === "Enter" || key === " ") {
      event.preventDefault();
      if (open) choose(active); else show();
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (!open) show();
      const now = Date.now();
      const text = (now - typed.current.at < 700 ? typed.current.text : "") + key.toLocaleLowerCase();
      typed.current = { text, at: now };
      const match = meetings.findIndex(meeting => meeting.name.toLocaleLowerCase().startsWith(text));
      if (match >= 0) setActive(match);
    }
  }
  function content(meeting) {
    return <><span className="meeting-option-name" title={meeting.name}>{meeting.name}</span><span className="meeting-option-time">{creationTime(meeting.createdAt)}</span></>;
  }
  return <div className="meeting-selector" ref={root} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <label id={`${id}-label`} htmlFor={`${id}-control`}>Open meeting</label>
    <button ref={control} id={`${id}-control`} type="button" role="combobox" aria-labelledby={`${id}-label`}
      aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-list`}
      aria-activedescendant={open && meetings[active] ? `${id}-option-${active}` : undefined}
      className="meeting-selector-control" onKeyDown={keyDown} onClick={() => { if (open) setOpen(false); else show(); }}>
      {current ? content(current) : <span>Choose a meeting</span>}
      <span className="meeting-selector-arrow" aria-hidden="true">▾</span>
    </button>
    {open && <ul ref={list} id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`} className="meeting-selector-options"
      style={{ maxHeight: placement.maxHeight, ...(placement.above ? { top: "auto", bottom: placement.controlHeight + 4, marginTop: 0 } : {}) }}>
      {meetings.map((meeting, index) => <li key={meeting.meetingId} id={`${id}-option-${index}`} role="option"
        aria-selected={meeting.meetingId === selected} aria-label={`${meeting.name} — ${creationTime(meeting.createdAt)}`}
        data-option-index={index} className={active === index ? "is-active" : undefined}
        onMouseDown={event => event.preventDefault()} onMouseMove={() => setActive(index)} onClick={() => choose(index)}>
        {content(meeting)}
      </li>)}
    </ul>}
  </div>;
}
